import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Activity, AlertTriangle, CheckCircle, XCircle, Router, Edit, Eye, CheckCircle2, Wifi, AlertCircle, ExternalLink, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OLTDevices = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResultOpen, setTestResultOpen] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    vendor: 'HSGQ',
    model: '',
    ip_address: '',
    port: 161,
    snmp_community: 'public',
    web_port: null,
    web_username: '',
    web_password: '',
    branch_id: '',
    location: '',
    description: '',
    number_of_pons: 8,  // Default 8 PON ports
    custom_temp_oid: '',  // Custom temperature OID
    temp_oid_enabled: false  // Enable custom temp OID
  });

  useEffect(() => {
    if (token) {
      fetchDevices();
      fetchBranches();
      // No polling - OLT status is checked on-demand when user clicks refresh
    }
  }, [token]);

  const fetchDevices = async (showLoading = false) => {
    if (showLoading) setRefreshing(true);
    try {
      const response = await axios.get(`${API_URL}/api/olt/devices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setDevices(response.data.data || []);
        if (showLoading) toast.success('Data OLT berhasil di-refresh');
      }
    } catch (error) {
      console.error('Error fetching OLT devices:', error);
      toast.error('Gagal memuat data OLT');
    } finally {
      if (showLoading) setRefreshing(false);
    }
  };
  
  const handleManualRefresh = () => {
    fetchDevices(true);
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setBranches(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = isEditOpen 
        ? `${API_URL}/api/olt/devices/${selectedDevice.id}`
        : `${API_URL}/api/olt/devices`;
      
      const method = isEditOpen ? 'put' : 'post';
      
      const response = await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(isEditOpen ? 'OLT berhasil diupdate' : 'OLT berhasil ditambahkan');
        setIsAddOpen(false);
        setIsEditOpen(false);
        fetchDevices();
        resetForm();
      }
    } catch (error) {
      console.error('Error saving OLT device:', error);
      toast.error('Gagal menyimpan OLT device');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (device) => {
    setSelectedDevice(device);
    setFormData({
      name: device.name,
      vendor: device.vendor,
      model: device.model,
      ip_address: device.ip_address,
      port: device.port,
      snmp_community: device.snmp_community || 'public',
      web_port: device.web_port || 6969,
      web_username: device.web_username || '',
      web_password: device.web_password || '',
      branch_id: device.branch_id || '',
      location: device.location || '',
      description: device.description || '',
      number_of_pons: device.number_of_pons || 8,
      custom_temp_oid: device.custom_temp_oid || '',
      temp_oid_enabled: device.temp_oid_enabled || false,
      service_profile: device.service_profile || '',
      line_profile: device.line_profile || ''
    });
    setIsEditOpen(true);
  };

  const handleAutoDetect = async () => {
    // Validate IP address
    if (!formData.ip_address) {
      toast.error('IP Address harus diisi terlebih dahulu');
      return;
    }

    setTestingConnection(true);
    try {
      // Call backend to detect OLT info via SNMP
      const response = await axios.post(`${API_URL}/api/olt/auto-detect`, {
        ip_address: formData.ip_address,
        port: formData.port || 161,
        snmp_community: formData.snmp_community || 'public'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const detected = response.data.data;
        
        // Auto-fill form with detected data
        setFormData(prev => ({
          ...prev,
          vendor: detected.vendor || prev.vendor,
          model: detected.model || prev.model,
          name: detected.name || prev.name,
          description: detected.description || prev.description,
          number_of_pons: detected.number_of_pons || prev.number_of_pons
        }));
        
        toast.success(`OLT terdeteksi: ${detected.vendor} ${detected.model}`);
      } else {
        toast.error(response.data.message || 'Gagal mendeteksi OLT');
      }
    } catch (error) {
      console.error('Auto-detect error:', error);
      toast.error('Gagal mendeteksi OLT via SNMP');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleTestConnection = async () => {
    // Validate required fields
    if (!formData.ip_address || !formData.vendor) {
      toast.error('IP Address dan Vendor harus diisi');
      return;
    }

    const testData = {
      ip_address: formData.ip_address,
      port: formData.port || 161,
      snmp_community: formData.snmp_community || 'public',
      web_port: formData.web_port || 6969,
      web_username: formData.web_username,
      web_password: formData.web_password,
      vendor: formData.vendor
    };

    setTestingConnection(true);
    try {
      const response = await axios.post(`${API_URL}/api/olt/test-connection`, testData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Save result and open dialog
      setTestResult({
        success: response.data.success,
        data: response.data.data || {},
        message: response.data.message,
        tested_ip: testData.ip_address,
        tested_port: testData.web_port
      });
      setTestResultOpen(true);
      
    } catch (error) {
      console.error('Error testing connection:', error);
      const errorMsg = error.response?.data?.message || 'Gagal test koneksi. Periksa IP, credentials, dan firewall.';
      
      // Show error in dialog
      setTestResult({
        success: false,
        data: {},
        message: errorMsg,
        error: true,
        tested_ip: testData.ip_address,
        tested_port: testData.web_port
      });
      setTestResultOpen(true);
    } finally {
      setTestingConnection(false);
    }
  };

  const handleDelete = async (deviceId) => {
    if (!window.confirm('Yakin ingin menghapus OLT device ini?')) return;

    try {
      const response = await axios.delete(`${API_URL}/api/olt/devices/${deviceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('OLT device berhasil dihapus');
        fetchDevices();
      }
    } catch (error) {
      console.error('Error deleting OLT device:', error);
      toast.error('Gagal menghapus OLT device');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      vendor: 'HSGQ',
      model: '',
      ip_address: '',
      port: 161,
      snmp_community: 'public',
      web_port: null,
      web_username: '',
      web_password: '',
      branch_id: '',
      location: '',
      description: '',
      number_of_pons: 8,
      custom_temp_oid: '',
      temp_oid_enabled: false,
      service_profile: '',
      line_profile: ''
    });
    setSelectedDevice(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'text-green-600 bg-green-50';
      case 'warning': return 'text-yellow-600 bg-yellow-50';
      case 'offline': return 'text-red-600 bg-red-50';
      default: return 'text-slate-600 bg-slate-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online': return <CheckCircle className="w-5 h-5" />;
      case 'warning': return <AlertTriangle className="w-5 h-5" />;
      case 'offline': return <XCircle className="w-5 h-5" />;
      default: return <Activity className="w-5 h-5" />;
    }
  };

  const getVendorColor = (vendor) => {
    const colors = {
      'HSGQ': 'bg-blue-100 text-blue-700',
      'HIOSO': 'bg-purple-100 text-purple-700',
      'VSOL': 'bg-green-100 text-green-700',
      'CDATA': 'bg-orange-100 text-orange-700',
      'ZTE': 'bg-red-100 text-red-700'
    };
    return colors[vendor] || 'bg-slate-100 text-slate-700';
  };

  const formatUptime = (seconds) => {
    // Display uptime from OLT device directly (no calculation)
    // This ensures we show the EXACT uptime from HSGQ/HIOSO native
    if (!seconds || seconds === 0) {
      return 'N/A';
    }
    
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    // Show format matching OLT native: "21d 8h 11m 49s"
    return `${days}d ${hours}h ${minutes}m ${secs}s`;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-3 lg:p-4">
      {/* Header - Compact */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-900 dark:text-slate-100">OLT Devices</h1>
          <p className="text-xs text-slate-600 mt-0.5 dark:text-slate-300">Kelola perangkat OLT dan monitoring</p>
        </div>
        <div className="flex gap-1.5">
          <Button 
            onClick={handleManualRefresh}
            disabled={refreshing}
            variant="outline"
            className="border-slate-300 h-8 text-xs dark:border-slate-600"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            onClick={() => setIsAddOpen(true)}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-md h-8 text-xs"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Tambah OLT
          </Button>
        </div>
      </div>

      {/* Stats Cards - Compact */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
        <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-lg p-2.5 shadow-sm">
          <div className="text-[10px] font-medium text-blue-600 uppercase tracking-wide mb-0.5 dark:text-blue-400">Total OLT</div>
          <div className="text-xl font-bold text-blue-600 dark:text-blue-400">{devices.length}</div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-white border border-green-200 rounded-lg p-2.5 shadow-sm">
          <div className="text-[10px] font-medium text-green-600 uppercase tracking-wide mb-0.5 dark:text-green-400">✓ Online</div>
          <div className="text-xl font-bold text-green-600 dark:text-green-400">{devices.filter(d => d.status === 'online').length}</div>
        </div>
        <div className="bg-gradient-to-br from-yellow-50 to-white border border-yellow-200 rounded-lg p-2.5 shadow-sm">
          <div className="text-[10px] font-medium text-yellow-600 uppercase tracking-wide mb-0.5 dark:text-yellow-400">⚠ Warning</div>
          <div className="text-xl font-bold text-yellow-600 dark:text-yellow-400">{devices.filter(d => d.status === 'warning').length}</div>
        </div>
        <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-lg p-2.5 shadow-sm dark:border-slate-700">
          <div className="text-[10px] font-medium text-slate-600 uppercase tracking-wide mb-0.5 dark:text-slate-300">Total ONTs</div>
          <div className="text-xl font-bold text-slate-900 dark:text-slate-100">{devices.reduce((sum, d) => sum + (d.total_onts || 0), 0)}</div>
        </div>
      </div>

      {/* OLT Devices Grid - Compact */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {devices.map((device) => (
          <Card key={device.id} className="shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-3">
              {/* Header - Compact */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <div className={`p-1.5 rounded-lg ${getStatusColor(device.status)}`}>
                    {getStatusIcon(device.status)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100">{device.name}</h3>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">{device.ip_address}</p>
                  </div>
                </div>
              </div>

              {/* Vendor Badge - Compact */}
              <div className="flex gap-1.5 mb-2">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${getVendorColor(device.vendor)}`}>
                  {device.vendor}
                </span>
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                  {device.model}
                </span>
              </div>

              {/* Stats - Compact */}
              <div className="grid grid-cols-2 gap-1.5 mb-2">
                <div className="bg-slate-50 rounded p-1.5 dark:bg-slate-900">
                  <div className="text-[10px] text-slate-500 dark:text-slate-400">ONTs Online</div>
                  <div className="text-xs font-bold text-slate-900 dark:text-slate-100">{device.online_onts}/{device.total_onts}</div>
                </div>
                <div className="bg-slate-50 rounded p-1.5 dark:bg-slate-900">
                  <div className="text-[10px] text-slate-500 dark:text-slate-400">Uptime</div>
                  <div className="text-xs font-bold text-slate-900 dark:text-slate-100">{formatUptime(device.uptime_seconds)}</div>
                </div>
              </div>

              {/* Location */}
              {device.location && (
                <p className="text-[10px] text-slate-600 mb-2 dark:text-slate-300">📍 {device.location}</p>
              )}

              {/* Actions - Compact */}
              <div className="flex gap-1.5 pt-2 border-t">
                <Button
                  onClick={() => navigate(`/app/olt/${device.id}`)}
                  size="sm"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 h-7 text-xs"
                >
                  <Eye className="w-3 h-3 mr-1" />
                  Monitor
                </Button>
                <Button
                  onClick={() => {
                    const webUrl = `http://${device.ip_address}:${device.web_port || 80}`;
                    window.open(webUrl, '_blank');
                  }}
                  size="sm"
                  variant="outline"
                  className="border-green-300 text-green-600 hover:bg-green-50 h-7 px-2 dark:text-green-400 dark:hover:bg-green-900/30"
                  title="Buka Web Interface OLT"
                >
                  <ExternalLink className="w-3 h-3" />
                </Button>
                <Button
                  onClick={() => handleEdit(device)}
                  size="sm"
                  variant="outline"
                  className="border-slate-300 h-7 px-2 dark:border-slate-600"
                >
                  <Edit className="w-3 h-3" />
                </Button>
                <Button
                  onClick={() => handleDelete(device.id)}
                  size="sm"
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50 h-7 px-2 dark:text-red-400 dark:hover:bg-red-900/30"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {devices.length === 0 && (
        <Card className="p-12 text-center">
          <Router className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 dark:text-slate-400">Belum ada OLT device. Klik "Tambah OLT" untuk menambahkan.</p>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isAddOpen || isEditOpen} onOpenChange={(open) => {
        if (!open) {
          setIsAddOpen(false);
          setIsEditOpen(false);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditOpen ? 'Edit OLT Device' : 'Tambah OLT Device'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nama OLT *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="OLT Depok Central"
                  required
                />
              </div>
              <div>
                <Label>Vendor *</Label>
                <select
                  value={formData.vendor}
                  onChange={(e) => setFormData({...formData, vendor: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm dark:border-slate-600"
                  required
                >
                  <option value="HSGQ">HSGQ</option>
                  <option value="HIOSO">HIOSO</option>
                  <option value="VSOL">VSOL</option>
                  <option value="CDATA">CDATA</option>
                  <option value="ZTE">ZTE</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Model *</Label>
                <Input
                  value={formData.model}
                  onChange={(e) => setFormData({...formData, model: e.target.value})}
                  placeholder="HG8310M"
                  required
                />
              </div>
              <div>
                <Label>IP Address *</Label>
                <Input
                  value={formData.ip_address}
                  onChange={(e) => setFormData({...formData, ip_address: e.target.value})}
                  placeholder="192.168.1.10 atau 103.179.252.66"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>SNMP Port</Label>
                <Input
                  type="number"
                  value={formData.port}
                  onChange={(e) => setFormData({...formData, port: parseInt(e.target.value)})}
                  placeholder="161"
                />
              </div>
              <div>
                <Label>SNMP Community</Label>
                <Input
                  value={formData.snmp_community}
                  onChange={(e) => setFormData({...formData, snmp_community: e.target.value})}
                  placeholder="public"
                />
              </div>
            </div>

            {/* Auto Detect Button */}
            <div className="flex justify-center py-2">
              <Button
                type="button"
                onClick={handleAutoDetect}
                disabled={testingConnection || !formData.ip_address}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {testingConnection ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Mendeteksi...
                  </>
                ) : (
                  <>
                    <Activity className="w-4 h-4 mr-2" />
                    Auto Detect dari SNMP
                  </>
                )}
              </Button>
            </div>

            <div className="border-t pt-4">
              <p className="text-xs text-slate-500 mb-3 dark:text-slate-400">
                ℹ️ Klik "Auto Detect dari SNMP" untuk mengisi Model, Vendor, dan informasi OLT secara otomatis
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Web Port (HTTP/HTTPS)</Label>
                <Input
                  type="number"
                  value={formData.web_port || ''}
                  onChange={(e) => setFormData({...formData, web_port: e.target.value ? parseInt(e.target.value) : null})}
                  placeholder="Kosongkan untuk auto-generate"
                />
                <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">
                  💡 Kosongkan untuk auto-generate port secara otomatis (6969, 6970, 6971, ...)
                </p>
              </div>
              <div>
                <Label>Web Username (untuk scraping HSGQ)</Label>
                <Input
                  value={formData.web_username}
                  onChange={(e) => setFormData({...formData, web_username: e.target.value})}
                  placeholder="admin"
                />
              </div>
              <div>
                <Label>Web Password (untuk scraping HSGQ)</Label>
                <Input
                  type="password"
                  value={formData.web_password}
                  onChange={(e) => setFormData({...formData, web_password: e.target.value})}
                  placeholder="••••••••"
                />
              </div>
              <div>
                <Label>Jumlah PON Ports *</Label>
                <select
                  value={formData.number_of_pons}
                  onChange={(e) => setFormData({...formData, number_of_pons: parseInt(e.target.value)})}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
                  required
                >
                  <option value={1}>1 PON</option>
                  <option value={2}>2 PON</option>
                  <option value={4}>4 PON</option>
                  <option value={8}>8 PON</option>
                  <option value={16}>16 PON</option>
                  <option value={32}>32 PON</option>
                </select>
                <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Jumlah PON port di OLT fisik</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Branch</Label>
                <select
                  value={formData.branch_id}
                  onChange={(e) => setFormData({...formData, branch_id: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm dark:border-slate-600"
                >
                  <option value="">Pilih Branch</option>
                  {branches.filter(b => b.is_active).map(branch => (
                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Lokasi</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  placeholder="Data Center Depok"
                />
              </div>
            </div>

            <div>
              <Label>Deskripsi</Label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm dark:border-slate-600"
                rows="3"
                placeholder="Deskripsi tambahan..."
              />
            </div>

            {/* Service Profile & Line Profile Configuration - MOVED UP FOR VISIBILITY */}
            <div className="border-4 border-blue-600 rounded-lg p-4 bg-blue-100 dark:bg-blue-900/40">
              <div className="flex items-center mb-3">
                <Router className="w-5 h-5 text-blue-600 mr-2 dark:text-blue-400" />
                <h3 className="font-semibold text-blue-900">Konfigurasi Profile ONU (Opsional)</h3>
              </div>
              <p className="text-xs text-blue-700 mb-3 dark:text-blue-300">
                ℹ️ Service Profile dan Line Profile digunakan untuk provisioning ONU secara otomatis. Pastikan profile yang dimasukkan sesuai dengan vendor OLT.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Service Profile</Label>
                  <Input
                    value={formData.service_profile || ''}
                    onChange={(e) => setFormData({...formData, service_profile: e.target.value})}
                    placeholder={
                      formData.vendor === 'ZTE' ? 'Contoh: INTERNET-100M' :
                      formData.vendor === 'HSGQ' ? 'Contoh: profile-100' :
                      formData.vendor === 'HIOSO' ? 'Contoh: SERVICE_100M' :
                      'Nama service profile'
                    }
                    className="text-sm"
                  />
                  <p className="text-xs text-blue-600 mt-1 dark:text-blue-400">
                    {formData.vendor === 'ZTE' && '🔹 ZTE: Service profile untuk bandwidth & VLAN'}
                    {formData.vendor === 'HSGQ' && '🔹 HSGQ: Profile ID atau nama profile'}
                    {formData.vendor === 'HIOSO' && '🔹 HIOSO: Service template name'}
                    {!['ZTE', 'HSGQ', 'HIOSO'].includes(formData.vendor) && '🔹 Nama atau ID service profile'}
                  </p>
                </div>
                <div>
                  <Label className="text-sm">Line Profile</Label>
                  <Input
                    value={formData.line_profile || ''}
                    onChange={(e) => setFormData({...formData, line_profile: e.target.value})}
                    placeholder={
                      formData.vendor === 'ZTE' ? 'Contoh: LINE-PROFILE-1' :
                      formData.vendor === 'HSGQ' ? 'Contoh: line-profile-1' :
                      formData.vendor === 'HIOSO' ? 'Contoh: LINE_DEFAULT' :
                      'Nama line profile'
                    }
                    className="text-sm"
                  />
                  <p className="text-xs text-blue-600 mt-1 dark:text-blue-400">
                    {formData.vendor === 'ZTE' && '🔹 ZTE: Line profile untuk physical layer'}
                    {formData.vendor === 'HSGQ' && '🔹 HSGQ: Line profile ID atau nama'}
                    {formData.vendor === 'HIOSO' && '🔹 HIOSO: Line template name'}
                    {!['ZTE', 'HSGQ', 'HIOSO'].includes(formData.vendor) && '🔹 Nama atau ID line profile'}
                  </p>
                </div>
              </div>
            </div>

            {/* Temperature OID Configuration */}
            <div className="border border-amber-200 rounded-lg p-4 bg-amber-50 dark:bg-amber-900/30">
              <div className="flex items-center mb-3">
                <Activity className="w-5 h-5 text-amber-600 mr-2 dark:text-amber-400" />
                <h3 className="font-semibold text-amber-900">Konfigurasi Temperature OID (Opsional)</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="temp_oid_enabled"
                    checked={formData.temp_oid_enabled || false}
                    onChange={(e) => setFormData({...formData, temp_oid_enabled: e.target.checked})}
                    className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500 dark:text-amber-400"
                  />
                  <Label htmlFor="temp_oid_enabled" className="cursor-pointer text-sm font-medium">
                    Gunakan Custom Temperature OID
                  </Label>
                </div>
                {formData.temp_oid_enabled && (
                  <div>
                    <Label className="text-sm">Custom Temperature OID</Label>
                    <Input
                      value={formData.custom_temp_oid || ''}
                      onChange={(e) => setFormData({...formData, custom_temp_oid: e.target.value})}
                      placeholder="Contoh: 1.3.6.1.4.1.3902.1012.3.1.1.1.8.1"
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-amber-700 mt-1 dark:text-amber-300">
                      📝 Masukkan SNMP OID spesifik untuk sensor temperature OLT Anda
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button 
                type="button" 
                onClick={handleTestConnection}
                disabled={testingConnection || !formData.ip_address}
                variant="outline"
                className="border-blue-500 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30"
              >
                {testingConnection ? 'Testing...' : '🔌 Test Koneksi'}
              </Button>
              <Button type="submit" disabled={loading} className="flex-1">
                {loading ? 'Menyimpan...' : 'Simpan'}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setIsAddOpen(false);
                  setIsEditOpen(false);
                  resetForm();
                }}
              >
                Batal
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Test Connection Result Dialog */}
      <Dialog open={testResultOpen} onOpenChange={setTestResultOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {testResult?.success ? (
                <>
                  <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                  <span>Test Koneksi Berhasil</span>
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                  <span>Test Koneksi Gagal</span>
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Test koneksi ke {testResult?.tested_ip}:{testResult?.tested_port}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* SNMP Status */}
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border dark:bg-slate-900">
              <Activity className="w-5 h-5 text-blue-600 mt-0.5 dark:text-blue-400" />
              <div className="flex-1">
                <div className="font-semibold text-sm text-slate-900 dark:text-slate-100">SNMP Connection</div>
                <div className="text-sm mt-1">
                  {testResult?.data?.snmp_status ? (
                    <span className={testResult.data.snmp_status.includes('Connected') ? 'text-green-600' : 'text-red-600'}>
                      {testResult.data.snmp_status}
                    </span>
                  ) : (
                    <span className="text-slate-500 dark:text-slate-400">Not tested</span>
                  )}
                </div>
                {!testResult?.data?.snmp_status?.includes('Connected') && (
                  <div className="text-xs text-slate-600 mt-1 dark:text-slate-300">
                    • Periksa IP dan Port (161)<br/>
                    • Pastikan SNMP enabled di OLT<br/>
                    • Cek SNMP Community benar
                  </div>
                )}
              </div>
            </div>

            {/* Web Status */}
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border dark:bg-slate-900">
              <Wifi className="w-5 h-5 text-indigo-600 mt-0.5 dark:text-indigo-400" />
              <div className="flex-1">
                <div className="font-semibold text-sm text-slate-900 dark:text-slate-100">Web Interface</div>
                <div className="text-sm mt-1">
                  {testResult?.data?.web_status ? (
                    <span className={testResult.data.web_status.includes('Connected') ? 'text-green-600' : testResult.data.web_status.includes('Skipped') ? 'text-slate-500' : 'text-red-600'}>
                      {testResult.data.web_status}
                    </span>
                  ) : (
                    <span className="text-slate-500 dark:text-slate-400">Not tested</span>
                  )}
                </div>
                {testResult?.data?.web_status && !testResult.data.web_status.includes('Connected') && !testResult.data.web_status.includes('Skipped') && (
                  <div className="text-xs text-slate-600 mt-1 dark:text-slate-300">
                    • Cek Web Username & Password<br/>
                    • Pastikan Web Port benar (default: 6969)<br/>
                    • Buka http://{testResult?.tested_ip}:{testResult?.tested_port} di browser untuk verify
                  </div>
                )}
              </div>
            </div>

            {/* Uptime if available */}
            {testResult?.data?.uptime && testResult.data.uptime !== 'N/A' && (
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200 dark:bg-green-900/30">
                <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                <div>
                  <div className="font-semibold text-sm text-green-900">OLT Uptime</div>
                  <div className="text-sm text-green-700 dark:text-green-300">{testResult.data.uptime}</div>
                </div>
              </div>
            )}

            {/* Error message if exists */}
            {testResult?.error && (
              <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200 dark:bg-red-900/30">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 dark:text-red-400" />
                <div className="flex-1">
                  <div className="font-semibold text-sm text-red-900">Error</div>
                  <div className="text-sm text-red-700 dark:text-red-300">{testResult.message}</div>
                </div>
              </div>
            )}

            {/* General recommendations */}
            {!testResult?.success && (
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200 dark:bg-amber-900/30">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 dark:text-amber-400" />
                <div className="flex-1">
                  <div className="font-semibold text-sm text-amber-900">Rekomendasi</div>
                  <div className="text-xs text-amber-700 mt-1 dark:text-amber-300">
                    1. Pastikan OLT bisa di-ping dari server<br/>
                    2. Cek firewall allow port 161 (SNMP) dan {testResult?.tested_port} (Web)<br/>
                    3. Verify credentials di OLT web interface<br/>
                    4. Jika masih gagal, Anda bisa Save dulu dan cek di menu Monitor
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setTestResultOpen(false)} className="w-full">
              Tutup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OLTDevices;
